import {COMPANY_ID} from '@constants/auth.constant'
import {LANDING} from '@constants/internal-route.constant'
import {useAuth} from '@context/Auth'
import {IUserFeature} from '@interface/auth.interface'
import {LocalStorageService} from '@services/utils/localStorage.service'
import {ReactNode} from 'react'
import {Navigate} from 'react-router-dom'

// Roles that bypass feature gating and see everything.
const SUPER_ROLES = ['COMPANY_ADMIN']

/** The four flags of an auth.role_features grant. `create` is the can_save column. */
export type PermAction = 'show' | 'create' | 'update' | 'delete'
export type FeaturePerms = Record<PermAction, boolean>

const ALL_PERMS: FeaturePerms = {show: true, create: true, update: true, delete: true}
const NO_PERMS: FeaturePerms = {show: false, create: false, update: false, delete: false}

/**
 * Reads the logged-in user's granted features (from sso verify-token → getUserAppAcl,
 * stored on userInfo) and answers menu/route permission questions.
 *
 * Resolves fail-CLOSED once the ACL is known: while verify-token is still in flight
 * (isChecking — the splash screen is shown) access is allowed so users aren't locked
 * out mid-load, but once the check resolves a missing/empty feed is DENIED (previously
 * it granted everything, a fail-open hole — review finding F1). COMPANY_ADMIN bypasses.
 */
export const usePermissions = () => {
  const {userInfo, isChecking} = useAuth()

  // Role is per-company — verify-token has no usable top-level roleKey, so resolve it from the
  // selected company (companies[].roleKey, falling back to roleName which carries the same value).
  const currentCid = LocalStorageService.get(COMPANY_ID)
  const currentCompany =
    userInfo?.companies?.find((c) => c.companyId === currentCid) || userInfo?.companies?.[0]
  const roleKey = userInfo?.roleKey || currentCompany?.roleKey || currentCompany?.roleName
  const isSuperAdmin = !!roleKey && SUPER_ROLES.includes(roleKey)
  const features = userInfo?.features
  const loaded = Array.isArray(features)

  // One row per feature (verify-token groups by feature id), keyed by url: the route constants in
  // @constants/internal-route.constant.ts are byte-identical to auth.features.url.
  const byUrl = new Map<string, IUserFeature>()
  ;(features || []).forEach((f) => f?.url && byUrl.set(f.url, f))

  /**
   * What the user may do on one screen, keyed by its route constant (= auth.features.url).
   *
   * Show is implied by any other flag, as on the server (core PermissionService) and on portal's
   * Role Permission screen. While verify-token is in flight (isChecking, splash shown) everything is
   * allowed so nobody is locked out mid-load; once it has answered, a feature that is absent is
   * DENIED. COMPANY_ADMIN may do everything.
   *
   * This decides what the UI offers. It is not the control: the service refuses with a 403 itself.
   */
  const perms = (url?: string): FeaturePerms => {
    if (isSuperAdmin || isChecking) return ALL_PERMS
    const f = loaded && url ? byUrl.get(url) : undefined
    if (!f) return NO_PERMS
    const create = Number(f.canSave) === 1
    const update = Number(f.canUpdate) === 1
    const del = Number(f.canDelete) === 1
    return {show: Number(f.canShow) === 1 || create || update || del, create, update, delete: del}
  }

  const can = (url: string | undefined, action: PermAction): boolean => perms(url)[action]

  const hasFeature = (url?: string): boolean => can(url, 'show')

  const hasAny = (urls: string[], action: PermAction = 'show'): boolean => {
    if (isSuperAdmin || isChecking) return true
    return urls.some((u) => can(u, action))
  }

  return {isSuperAdmin, roleKey, features: features || [], loaded, perms, can, hasFeature, hasAny}
}

/**
 * Route guard: renders children only if the user may do `action` (Show by default) on at least
 * one of the `anyOf`
 * feature URLs, otherwise redirects to the landing router (which forwards to a screen they hold).
 */
export const RequireFeature = ({
  anyOf,
  action = 'show',
  children,
}: {
  anyOf: string[]
  action?: PermAction
  children: ReactNode
}) => {
  const {hasAny} = usePermissions()
  return hasAny(anyOf, action) ? <>{children}</> : <Navigate to={LANDING} replace />
}

/**
 * Renders children only when the user may do `action` on the screen `feature` (its route constant,
 * = auth.features.url), otherwise `fallback` (nothing by default). For a single button; a list
 * page reads perms(ROUTE) once and passes handlers conditionally instead.
 */
export const Can = ({
  feature,
  action,
  children,
  fallback = null,
}: {
  feature?: string
  action: PermAction
  children: ReactNode
  fallback?: ReactNode
}) => {
  const {can} = usePermissions()
  return <>{can(feature, action) ? children : fallback}</>
}
